import React, { useState } from 'react';
import {
  Typography, List, Avatar, Form, Button, Input, message, Spin,
} from 'antd';
import { UserOutlined, SendOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useGetCommentsQuery, useCreateCommentMutation } from '../deals/dealsApi';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { TextArea } = Input;
const { Text } = Typography;

export default function CommentSection({ dealPublicId }) {
  const [form] = Form.useForm();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);

  const { data, isLoading: commentsLoading, refetch } = useGetCommentsQuery({ dealId: dealPublicId });
  const [createComment] = useCreateCommentMutation();

  const handleSubmit = async (values) => {
    if (!isAuthenticated) return message.warning('Sign in to comment');
    setLoading(true);
    try {
      await createComment({ dealId: dealPublicId, body: values.body }).unwrap();
      form.resetFields();
      message.success('Comment posted');
      refetch();
    } catch (err) {
      message.error('Failed to post comment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
        }}
      >
        <Text
          strong
          style={{
            fontSize: 16,
            color: 'var(--df-text)',
            fontFamily: 'var(--df-font-display)',
          }}
        >
          Comments ({data?.items?.length || 0})
        </Text>
      </div>

      {isAuthenticated && (
        <Form form={form} onFinish={handleSubmit} style={{ marginBottom: 24 }}>
          <Form.Item name="body" rules={[{ required: true, message: 'Comment cannot be empty' }]}>
            <TextArea
              rows={3}
              placeholder="Share your thoughts on this deal…"
              style={{ borderRadius: 8 }}
              aria-label="Write a comment"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SendOutlined />}
              style={{
                borderRadius: 8,
                fontWeight: 600,
                height: 40,
                background: 'var(--df-accent)',
                borderColor: 'var(--df-accent)',
              }}
            >
              Post comment
            </Button>
          </Form.Item>
        </Form>
      )}

      {!isAuthenticated && (
        <Text style={{ display: 'block', marginBottom: 20, color: 'var(--df-text-secondary)', fontSize: 13 }}>
          Sign in to join the conversation.
        </Text>
      )}

      {commentsLoading ? (
        <div style={{ textAlign: 'center', padding: 30 }}>
          <Spin tip="Loading comments…" />
        </div>
      ) : !data?.items?.length ? (
        <Text
          style={{
            fontSize: 14,
            display: 'block',
            textAlign: 'center',
            padding: '28px 0',
            color: 'var(--df-text-muted)',
          }}
        >
          No comments yet. Be the first to share your thoughts.
        </Text>
      ) : (
        <List
          dataSource={data.items}
          renderItem={(item) => (
            <div
              style={{
                display: 'flex',
                gap: 12,
                padding: '14px 0',
                borderBottom: '1px solid var(--df-border-subtle)',
              }}
            >
              <Avatar
                src={item.author?.avatarUrl}
                icon={<UserOutlined />}
                style={{
                  background: item.author?.avatarUrl ? 'transparent' : 'rgba(11, 58, 92, 0.1)',
                  color: 'var(--df-primary)',
                  flexShrink: 0,
                }}
                size={36}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                  <Text strong style={{ fontSize: 13, color: 'var(--df-text)' }}>
                    {item.author?.displayName || 'Anonymous'}
                  </Text>
                  <Text style={{ fontSize: 12, color: 'var(--df-text-muted)' }}>
                    · {dayjs(item.createdAt).fromNow()}
                  </Text>
                </div>
                <Text style={{ fontSize: 14, color: 'var(--df-text-secondary)', lineHeight: 1.55, display: 'block' }}>
                  {item.body}
                </Text>
              </div>
            </div>
          )}
        />
      )}
    </div>
  );
}
